import { diContainer } from '@/coreContainer/container';
import { TableType } from '@/coreDomainsModule/table';
import { onMounted, ref, Ref } from 'vue';
import { FilterContentItem } from '../filterModal/filterModal';
import { FilterType, isFilterType } from '@/coreDomainsModule/entities/filter';
import { INCLUDE_NULL_MARKER } from '@/corePagesModule/constants/filterMarkers';

/**
 * フィルター情報を管理する
 * @param tableType - テーブルタイプ
 * @param syncingTableTypeList - 当該のテーブルタイプとフィルター情報を同期したいテーブルタイプ
 */
export const useTableFilter = (tableType: TableType, syncingTableTypeList?: TableType[]) => {
  const cachedFilterItems: Ref<FilterContentItem[]> = ref([]);

  /**
   * テーブルフィルターをinitializeする
   */
  const initTableFilter = async () => {
    await _updateFilterItemsCache();
  };

  /**
   * フィルターアイテムを返す
   * FilterModalに設定済みのフィルターを渡す際、関数の形式で渡さないとフェッチ後に情報が更新されなくなってしまうため
   * filterItemsを返す関数を作成しています。
   * 上記の問題が解決すれば、この関数は不要になります。
   */
  const getFilterItemsValue = () => cachedFilterItems;

  /**
   * フィルター情報をIndexedDBに保存する
   * @param propFilterItems - 保存するフィルター情報
   */
  const bulkUpdateFilterInfos = async (propFilterItems: FilterContentItem[]) => {
    await diContainer.bulkSaveTableFilterPreferenceUseCase.execute(tableType, propFilterItems);
    if (syncingTableTypeList && syncingTableTypeList.length) {
      // 対象のテーブルとフィルターを同期したいテーブルの処理
      for (const syncingTableType of syncingTableTypeList) {
        await diContainer.bulkSaveTableFilterPreferenceUseCase.execute(syncingTableType, propFilterItems);
      }
    }
    // DBの情報を正として、関数内のfilterItemsはキャッシュとして使用しているのでここでDBをフェッチする
    await _updateFilterItemsCache();
  };

  /**
   * フィルター情報のキャッシュを更新する
   */
  const _updateFilterItemsCache = async () => {
    const getFilterInfosResult = await diContainer.getTableFilterPreferenceUseCase.execute(tableType);
    if (getFilterInfosResult.status.type !== 'success') {
      if (getFilterInfosResult.status.error.message === 'preferenceの取得に失敗しました') {
        // preferenceが存在しない場合
        console.error('個人設定の取得に失敗しました。サーバーからのデータが存在しない可能性があります。');
      } else {
        // 対象のテーブルタイプのフィルター情報が存在しない場合
        // このケースでは、空のフィルター情報をputして空のフィルター情報を返す
        console.error('ヘッダーの取得に失敗しました。tableType:', tableType, 'Error:', getFilterInfosResult.status.error);
        // デフォルト表示ヘッダーリストを設定 (非表示考慮)
        // 空のFilterInfoをDBに保存
        await _resetFilterInfo();
      }
      cachedFilterItems.value = [];
      return;
    }
    const filterInfoResultEntries = Object.entries(getFilterInfosResult.status.data);
    if (!filterInfoResultEntries.every(([key]) => isFilterType(key))) {
      console.error('フィルター情報が破損しています。フィルタ情報をリセットします。');
      await _resetFilterInfo();
      cachedFilterItems.value = [];
      return;
    }
    const filterItemsResult: FilterContentItem[] = Object.entries(getFilterInfosResult.status.data).map(([key, value]) => {
      const isLastCheckedAt = key === 'LAST_CHECKED_AT';
      const hasIncludeNull = isLastCheckedAt && value.includes(INCLUDE_NULL_MARKER);
      const selectedValue = hasIncludeNull ? value.filter((item) => item !== INCLUDE_NULL_MARKER) : value;
      return {
        filterType: key as FilterType,
        selectedValue,
        label: '',
        includeNull: isLastCheckedAt ? hasIncludeNull : undefined,
      };
    });
    cachedFilterItems.value = filterItemsResult;
    return;
  };

  /**
   * フィルター情報をリセットする
   */
  const _resetFilterInfo = async () => {
    await diContainer.bulkSaveTableFilterPreferenceUseCase.execute(tableType, []);
    if (syncingTableTypeList && syncingTableTypeList.length) {
      // 対象のテーブルとフィルターを同期したいテーブルの処理
      for (const syncingTableType of syncingTableTypeList) {
        await diContainer.bulkSaveTableFilterPreferenceUseCase.execute(syncingTableType, []);
      }
    }
  };

  onMounted(async () => {
    await initTableFilter();
  });

  return { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter };
};
